<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 p-4"
    @click.self="close"
  >
    <div
      class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-full overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="noticia-images-title"
    >
      <div class="flex items-center justify-between px-6 pt-5 pb-3">
        <h2 id="noticia-images-title" class="text-lg font-semibold text-gray-800">
          Gestionar Imágenes - {{ post.title }}
        </h2>
        <button type="button" class="text-gray-400 hover:text-gray-600 text-xl leading-none" aria-label="Cerrar" @click="close">
          &times;
        </button>
      </div>

      <div class="px-6 pb-6">
        <p v-if="error" class="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{{ error }}</p>
        <p v-if="notice" class="mb-3 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 dark:text-yellow-200 rounded-lg text-sm">{{ notice }}</p>

        <!-- Zona de carga -->
        <div
          :class="[
            'flex flex-col items-center justify-center text-center px-4 py-10 border-2 border-dashed rounded-lg transition-colors',
            remaining === 0
              ? 'border-gray-200 opacity-60 cursor-not-allowed'
              : dragging
                ? 'border-blue-500 bg-blue-50 cursor-pointer'
                : 'border-gray-300 hover:border-gray-400 cursor-pointer',
          ]"
          @click="openPicker"
          @dragover.prevent="dragging = remaining > 0"
          @dragleave.prevent="dragging = false"
          @drop.prevent="onDrop"
        >
          <font-awesome-icon :icon="['fas', 'file-arrow-up']" class="text-3xl text-gray-400 mb-3" />
          <p class="text-sm text-gray-600">
            {{ remaining === 0 ? 'Se alcanzó el máximo de imágenes' : 'Haz clic para seleccionar imágenes o arrastra y suelta' }}
          </p>
          <p class="text-xs text-gray-400 mt-1">Formatos soportados: JPEG, PNG, GIF, WebP</p>
          <input
            ref="picker"
            type="file"
            multiple
            :accept="acceptAttr"
            class="hidden"
            :disabled="remaining === 0"
            @change="onPick"
          />
        </div>

        <p class="mt-3 text-xs text-gray-500 text-right">
          {{ existingCount + selected.length }} de {{ maxImages }} imágenes
          <span v-if="existingCount">({{ existingCount }} ya guardadas)</span>
        </p>

        <!-- Seleccionadas -->
        <p v-if="!selected.length" class="py-6 text-sm text-center text-gray-500">No hay imágenes seleccionadas</p>
        <ul v-else class="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <li v-for="item in selected" :key="item.id" class="relative">
            <img :src="item.preview" :alt="item.file.name" class="w-full h-28 object-cover rounded-md border border-gray-200" />
            <button
              type="button"
              class="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-600 text-white text-sm leading-none hover:bg-red-700"
              :aria-label="`Quitar ${item.file.name}`"
              @click="removeSelected(item.id)"
            >
              &times;
            </button>
            <p class="mt-1 text-xs text-gray-500 truncate" :title="item.file.name">{{ item.file.name }}</p>
          </li>
        </ul>

        <div class="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200">
          <button
            type="button"
            :disabled="saving"
            class="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
            @click="close"
          >
            Cancelar
          </button>
          <button
            type="button"
            :disabled="saving || !selected.length"
            class="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            @click="save"
          >
            {{ saving ? 'Guardando…' : 'Guardar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { blogPostImagesApi } from '@/service';
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGES_PER_POST, MAX_UPLOAD_BYTES, apiErrorMessage } from '@/utils/blog';

let nextId = 0;

export default {
  name: 'NoticiaImagesModal',
  props: {
    // Noticia con su array `images` actual.
    post: { type: Object, required: true },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      selected: [],
      dragging: false,
      saving: false,
      error: '',
      notice: '',
      maxImages: MAX_IMAGES_PER_POST,
      acceptAttr: ACCEPTED_IMAGE_TYPES.join(','),
    };
  },
  computed: {
    existingCount() {
      return (this.post.images || []).length;
    },
    remaining() {
      return Math.max(0, this.maxImages - this.existingCount - this.selected.length);
    },
  },
  beforeUnmount() {
    this.selected.forEach((item) => URL.revokeObjectURL(item.preview));
  },
  methods: {
    close() {
      if (!this.saving) this.$emit('close');
    },
    openPicker() {
      if (this.remaining > 0) this.$refs.picker.click();
    },
    onPick(event) {
      this.addFiles(Array.from(event.target.files || []));
      event.target.value = '';
    },
    onDrop(event) {
      this.dragging = false;
      if (this.remaining > 0) this.addFiles(Array.from(event.dataTransfer?.files || []));
    },
    addFiles(files) {
      this.error = '';
      this.notice = '';
      const messages = [];

      const valid = files.filter((file) => ACCEPTED_IMAGE_TYPES.includes(file.type));
      if (valid.length < files.length) {
        messages.push('Se ignoraron archivos con formato no soportado (solo JPEG, PNG, GIF o WebP).');
      }

      const accepted = valid.slice(0, this.remaining);
      if (accepted.length < valid.length) {
        messages.push(`Solo se permiten ${this.maxImages} imágenes por noticia; se descartaron ${valid.length - accepted.length}.`);
      }

      accepted.forEach((file) => {
        this.selected.push({ id: ++nextId, file, preview: URL.createObjectURL(file) });
      });
      this.notice = messages.join(' ');
    },
    removeSelected(id) {
      const index = this.selected.findIndex((item) => item.id === id);
      if (index === -1) return;
      URL.revokeObjectURL(this.selected[index].preview);
      this.selected.splice(index, 1);
      this.notice = '';
    },
    async save() {
      this.error = '';
      const totalBytes = this.selected.reduce((sum, item) => sum + item.file.size, 0);
      if (totalBytes > MAX_UPLOAD_BYTES) {
        this.error = 'El tamaño total de las imágenes supera los 32 MB. Quita algunas e intenta de nuevo.';
        return;
      }
      this.saving = true;
      try {
        await blogPostImagesApi.upload(this.post.id, this.selected.map((item) => item.file));
        this.$emit('saved');
      } catch (e) {
        this.error = apiErrorMessage(e, 'No se pudieron subir las imágenes.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
